// Native GPS watcher — Capacitor BackgroundGeolocation wrapper that
// mirrors the browser Geolocation watcher interface so the useGpsTracker
// hook can swap seamlessly.
//
// The @capacitor-community/background-geolocation plugin keeps the GPS
// alive even when the app is backgrounded or the screen is locked, which
// is the whole reason RIVL moved to a native shell. See use-gps.js for how
// this is consumed.
//
// On web this module exposes no-op stubs — the caller falls back to
// navigator.geolocation.

import { registerPlugin } from '@capacitor/core';
import { isNative } from './native';

// Dynamic plugin ref so we don't bundle Capacitor-only code into the web
// build. registerPlugin is a no-op proxy on web but we still want to
// avoid awaiting it there.
const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');

/**
 * startWatch(onFix, onError) → watcherId (string) | null
 *   onFix(position) is called with { coords: { latitude, longitude,
 *     accuracy, altitude?, speed? }, timestamp }
 *   onError(err) is called with a plain Error.
 *
 * The native plugin keeps a sticky notification on Android ("RIVL is
 * tracking your run") and uses iOS significant-location-change + full
 * precision while the race is live.
 */
export async function startNativeWatch(onFix, onError) {
  if (!isNative()) return null;
  try {
    const id = await BackgroundGeolocation.addWatcher(
      {
        // Required strings for the Android foreground service notification
        backgroundMessage: 'RIVL is tracking your run',
        backgroundTitle: 'RIVL is running',
        requestPermissions: true,
        stale: false,
        distanceFilter: 2, // metres between fixes (match web floor)
      },
      (location, error) => {
        if (error) {
          if (error.code === 'NOT_AUTHORIZED') {
            try { BackgroundGeolocation.openSettings(); } catch { /* noop */ }
          }
          try { onError?.(new Error(error.message || 'GPS error')); } catch { /* noop */ }
          return;
        }
        if (!location) return;
        try {
          onFix?.({
            coords: {
              latitude: location.latitude,
              longitude: location.longitude,
              accuracy: location.accuracy,
              altitude: location.altitude,
              speed: location.speed,
            },
            timestamp: location.time,
          });
        } catch { /* noop */ }
      }
    );
    return id;
  } catch (err) {
    try { onError?.(err instanceof Error ? err : new Error(String(err))); } catch { /* noop */ }
    return null;
  }
}

export async function stopNativeWatch(watcherId) {
  if (!watcherId || !isNative()) return;
  try { await BackgroundGeolocation.removeWatcher({ id: watcherId }); } catch { /* noop */ }
}
